import type { ApolloClient } from '@apollo/client';
import { useApolloClient } from '@apollo/client';

import type { Host, Xinglet, XingletFunction } from '@xing-com/crate-xinglet';
import type { Runtime } from '@xing-com/crate-xinglet/internal';

import { BrowserRoot } from './browser/root';
import { ServerRoot } from './server/root';
import type { HopsUploadRequestMutationVariables } from './upload-request-mutation.gql-types';

export interface CoreRootExtension {
  executeCommand:
    | XingletFunction<HopsEnvironment, '@xing-com/crate-core-root.apolloClient'>
    | XingletFunction<HopsEnvironment, '@xing-com/crate-core-root.uploadFile'>;
}

export type HopsEnvironmentProps = React.PropsWithChildren;

export default class HopsEnvironment implements Xinglet {
  private client?: ApolloClient<unknown>;

  public '@xing-com/crate-core-root.apolloClient'():
    | ApolloClient<unknown>
    | undefined {
    return this.client;
  }

  public async '@xing-com/crate-core-root.uploadFile'(
    application: HopsUploadRequestMutationVariables['application'],
    file: File,
    errorHandler?: (id: string) => void
  ): Promise<string | null> {
    if (!this.client) {
      throw new Error('No upload outside of hops environment');
    }
    return await (
      await import('./upload')
    ).uploadFile(this.client, application, file, errorHandler);
  }

  public getComponent(
    host: Host,
    runtime: Runtime
  ): React.ComponentType<HopsEnvironmentProps> {
    return ({ children }) => {
      this.client = useApolloClient();

      if (runtime.isServer) {
        return <ServerRoot runtime={runtime}>{children}</ServerRoot>;
      } else {
        return <BrowserRoot>{children}</BrowserRoot>;
      }
    };
  }
}
